import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Card, Col, Drawer, Form, Input, InputNumber, Modal, Pagination, Row, Select, Space, Switch, Table, Tag, message } from 'antd';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPublishAccount, deletePublishAccounts, getPublishAccountDetail, searchPublishAccounts, updatePublishAccount, xhsMcpEnsure } from '../api';
import { useAuth } from '../auth/AuthContext';
import { fixMojibakeUtf8 } from '../utils/text';
import { resolveErrorMessage } from '../utils/errorMessage';
import XhsAccountCheckButton from '../components/XhsAccountCheckButton';
import SocialAccountLoginButton from '../components/SocialAccountLoginButton';
import DouyinAccountCheckButton from '../components/DouyinAccountCheckButton';
import KuaishouAccountCheckButton from '../components/KuaishouAccountCheckButton';
import WeixinChannelsAccountCheckButton from '../components/WeixinChannelsAccountCheckButton';

const AccountManagement = () => {
  const [form] = Form.useForm();
  const [detailForm] = Form.useForm();
  const { user } = useAuth();

  const [dataSource, setDataSource] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });

  const [selectedAccountKeys, setSelectedAccountKeys] = useState([]);

  const [drawerVisible, setDrawerVisible] = useState(false);
  const [drawerMode, setDrawerMode] = useState('create'); // create | edit
  const [detailLoading, setDetailLoading] = useState(false);
  const [xhsProvisioning, setXhsProvisioning] = useState(false);
  const [xhsProvisionAccount, setXhsProvisionAccount] = useState(null);
  const [socialProvisionAccount, setSocialProvisionAccount] = useState(null);
  const paginationRef = useRef(pagination);
  const recentlyUpdatedAccountsRef = useRef(new Map());

  const platformOptions = useMemo(() => ([
    { label: '微信公众号', value: '微信公众号' },
    { label: '微信视频号', value: '微信视频号' },
    { label: '小红书', value: '小红书' },
    { label: '抖音', value: '抖音' },
    { label: '快手', value: '快手' },
    { label: '百家号', value: '百家号' },
    { label: '知乎', value: '知乎' },
    { label: 'B站', value: 'B站' },
    { label: '今日头条', value: '今日头条' },
  ]), []);

  const statusMap = {
    active: { text: '活跃', color: 'green' },
    paused: { text: '已暂停', color: 'orange' },
    inactive: { text: '未激活', color: 'default' },
    suspended: { text: '已停用', color: 'red' },
  };

  const formatDateTime = (dateTimeString) => {
    if (!dateTimeString) return '-';
    try {
      const date = new Date(dateTimeString);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    } catch (error) {
      return dateTimeString;
    }
  };

  const formatListField = (value) => {
    if (value === null || value === undefined) return '';
    const items = Array.isArray(value) ? value : [value];
    const text = items
      .filter((item) => item !== null && item !== undefined)
      .map((item) => String(item).trim())
      .filter(Boolean)
      .join(', ');
    return text;
  };

  const parseCommaList = (value) => {
    if (value === null || value === undefined) return undefined;
    if (Array.isArray(value)) return value;
    const text = String(value).trim();
    if (!text) return undefined;
    return text
      .split(/[,，]/)
      .map((item) => item.trim())
      .filter(Boolean);
  };

  const normalizeOptionalString = (value) => {
    if (value === null || value === undefined) return undefined;
    const text = String(value);
    return text.trim() ? text.trim() : undefined;
  };

  const parseJsonObject = (value) => {
    const text = normalizeOptionalString(value);
    if (!text) return undefined;
    try {
      const parsed = JSON.parse(text);
      if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
        throw new Error('platform_config 必须是 JSON object');
      }
      return parsed;
    } catch (error) {
      throw new Error(`platform_config JSON 解析失败：${error.message}`);
    }
  };

  const buildSearchParams = (formValues = {}, page = 1, pageSize = 20) => {
    const params = {
      page,
      page_size: pageSize,
      keyword: normalizeOptionalString(formValues.keyword) || '',
      filters: {},
      sort: { field: 'created_at', order: 'desc' },
    };

    if (Array.isArray(formValues.platforms) && formValues.platforms.length > 0) {
      params.filters.platforms = formValues.platforms;
    }

    if (Array.isArray(formValues.status) && formValues.status.length > 0) {
      params.filters.status = formValues.status;
    }

    if (normalizeOptionalString(formValues.operator_user_id)) {
      params.filters.operator_user_id = normalizeOptionalString(formValues.operator_user_id);
    }

    if (Object.keys(params.filters).length === 0) delete params.filters;
    return params;
  };

  useEffect(() => {
    paginationRef.current = pagination;
  }, [pagination]);

  const loadData = useCallback(async (searchParams = {}, page = null, pageSize = null) => {
    setLoading(true);
    try {
      const currentPage = page !== null ? page : paginationRef.current.current;
      const currentPageSize = pageSize !== null ? pageSize : paginationRef.current.pageSize;

      const params = {
        ...searchParams,
        page: currentPage,
        page_size: currentPageSize,
      };

      const response = await searchPublishAccounts(params);
      if (response.status === 'success') {
        const ttlMs = 15000;
        const now = Date.now();
        for (const [id, meta] of recentlyUpdatedAccountsRef.current.entries()) {
          if (!meta?.ts || now - meta.ts > ttlMs) recentlyUpdatedAccountsRef.current.delete(id);
        }

        const list = Array.isArray(response.items) ? response.items : [];
        const merged = list.map((item) => {
          const accountId = item?.account_id;
          if (!accountId) return item;
          const meta = recentlyUpdatedAccountsRef.current.get(accountId);
          if (!meta?.patch) return item;
          return { ...item, ...meta.patch };
        });

        setDataSource(merged);
        setPagination({
          current: response.page || currentPage,
          pageSize: response.page_size || currentPageSize,
          total: response.total || 0,
        });
      } else {
        message.error(resolveErrorMessage(response.error_message, '加载账号列表失败'));
      }
    } catch (error) {
      console.error('加载账号列表失败:', error);
      message.error('加载账号列表失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData({});
  }, [loadData]);

  const handleSearch = () => {
    const formValues = form.getFieldsValue();
    const searchParams = buildSearchParams(formValues, 1, pagination.pageSize);
    setSelectedAccountKeys([]);
    loadData(searchParams, 1, pagination.pageSize);
  };

  const handleFilterFinish = (values) => {
    const searchParams = buildSearchParams(values, 1, pagination.pageSize);
    setSelectedAccountKeys([]);
    loadData(searchParams, 1, pagination.pageSize);
  };

  const openCreateDrawer = () => {
    setDrawerMode('create');
    setDrawerVisible(true);
    setDetailLoading(false);
    setXhsProvisioning(false);
    setXhsProvisionAccount(null);
    detailForm.resetFields();
    detailForm.setFieldsValue({
      status: 'active',
      login_after_create: true,
      operator_user_ids: user?.user_id ? String(user.user_id) : undefined,
      operator_user_names: user?.nickname ? String(fixMojibakeUtf8(user.nickname)) : undefined,
    });
  };

  const openEditDrawer = async (record) => {
    setDrawerMode('edit');
    setDrawerVisible(true);
    setDetailLoading(true);
    detailForm.resetFields();

    try {
      const response = await getPublishAccountDetail(record.account_id);
      if (response.status === 'success') {
        const account = response.account || record;
        detailForm.setFieldsValue({
          account_id: account.account_id,
          platform: account.platform,
          account_name: account.account_name,
          account_type: account.account_type,
          status: account.status,
          operator_user_ids: formatListField(account.operator_user_ids),
          operator_user_names: formatListField(account.operator_user_names),
          auth_config_id: account.auth_config_id,
          platform_config: account.platform_config ? JSON.stringify(account.platform_config, null, 2) : '',
        });
      } else {
        message.error('账号不存在或已被删除');
        setDrawerVisible(false);
      }
    } catch (error) {
      console.error('加载账号详情失败:', error);
      message.error('加载账号详情失败，请稍后重试');
      setDrawerVisible(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleDelete = (record) => {
    const reloadListAfterDelete = (deletedAccountIds = []) => {
      const ids = Array.from(new Set((deletedAccountIds || []).filter(Boolean)));
      const currentPageIds = (dataSource || []).map((item) => item.account_id).filter(Boolean);
      const deletedOnCurrentPage = ids.filter((id) => currentPageIds.includes(id)).length;
      const shouldBackPage = pagination.current > 1 && currentPageIds.length > 0 && deletedOnCurrentPage >= currentPageIds.length;
      const nextPage = shouldBackPage ? pagination.current - 1 : pagination.current;

      const formValues = form.getFieldsValue();
      const searchParams = buildSearchParams(formValues, nextPage, pagination.pageSize);
      loadData(searchParams, nextPage, pagination.pageSize);
    };

    Modal.confirm({
      title: '确认删除',
      content: `确定要删除账号 "${record.account_name || record.account_id}" 吗？此操作不可恢复。`,
      okText: '确定',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        try {
          message.loading({ content: '正在删除...', key: 'deleteAccount' });
          const response = await deletePublishAccounts(record.account_id);
          if (response.status === 'success') {
            const deletedCount = Array.isArray(response.deleted) ? response.deleted.length : 0;
            const failedCount = Array.isArray(response.failed) ? response.failed.length : 0;
            if (failedCount === 0) {
              message.success({ content: `删除成功（${deletedCount}）`, key: 'deleteAccount' });
            } else {
              message.warning({ content: `删除完成：成功 ${deletedCount}，失败 ${failedCount}`, key: 'deleteAccount' });
            }
            setSelectedAccountKeys((prev) => (prev || []).filter((id) => id !== record.account_id));
            reloadListAfterDelete([record.account_id]);
          } else {
            message.error({ content: resolveErrorMessage(response.error_message, '删除失败'), key: 'deleteAccount' });
          }
        } catch (error) {
          console.error('删除失败:', error);
          message.error({ content: '删除失败，请稍后重试', key: 'deleteAccount' });
        }
      },
    });
  };

  const handleBatchDelete = () => {
    if (!selectedAccountKeys.length) {
      message.warning('请先选择要删除的账号');
      return;
    }

    const accountIds = [...selectedAccountKeys];
    const reloadListAfterDelete = (deletedAccountIds = []) => {
      const ids = Array.from(new Set((deletedAccountIds || []).filter(Boolean)));
      const currentPageIds = (dataSource || []).map((item) => item.account_id).filter(Boolean);
      const deletedOnCurrentPage = ids.filter((id) => currentPageIds.includes(id)).length;
      const shouldBackPage = pagination.current > 1 && currentPageIds.length > 0 && deletedOnCurrentPage >= currentPageIds.length;
      const nextPage = shouldBackPage ? pagination.current - 1 : pagination.current;

      const formValues = form.getFieldsValue();
      const searchParams = buildSearchParams(formValues, nextPage, pagination.pageSize);
      loadData(searchParams, nextPage, pagination.pageSize);
    };

    Modal.confirm({
      title: '确认批量删除',
      content: `确定要删除选中的 ${accountIds.length} 个账号吗？此操作不可恢复。`,
      okText: '确定',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        try {
          message.loading({ content: `正在删除 ${accountIds.length} 个账号...`, key: 'batchDeleteAccount' });
          const response = await deletePublishAccounts(accountIds);
          if (response.status === 'success') {
            const deletedIds = Array.isArray(response.deleted) ? response.deleted : [];
            const failedItems = Array.isArray(response.failed) ? response.failed : [];
            const deletedCount = deletedIds.length;
            const failedCount = failedItems.length;

            setSelectedAccountKeys([]);

            if (failedCount === 0) {
              message.success({ content: `成功删除 ${deletedCount} 个账号`, key: 'batchDeleteAccount' });
            } else {
              message.warning({ content: `删除完成：成功 ${deletedCount}，失败 ${failedCount}`, key: 'batchDeleteAccount', duration: 5 });
            }

            reloadListAfterDelete(accountIds);
          } else {
            message.error({ content: resolveErrorMessage(response.error_message, '批量删除失败'), key: 'batchDeleteAccount' });
          }
        } catch (error) {
          console.error('批量删除失败:', error);
          message.error({ content: '批量删除失败，请稍后重试', key: 'batchDeleteAccount' });
        }
      },
    });
  };

  const currentPageAccountIds = useMemo(
    () => (dataSource || []).map((item) => item.account_id).filter(Boolean),
    [dataSource]
  );

  const isAllCurrentPageSelected = useMemo(
    () => currentPageAccountIds.length > 0 && currentPageAccountIds.every((id) => (selectedAccountKeys || []).includes(id)),
    [currentPageAccountIds, selectedAccountKeys]
  );

  const toggleSelectAllCurrentPage = () => {
    if (currentPageAccountIds.length === 0) return;
    setSelectedAccountKeys((prevKeys) => {
      const otherPageSelected = (prevKeys || []).filter((key) => !currentPageAccountIds.includes(key));
      const prevAllCurrent = currentPageAccountIds.every((id) => (prevKeys || []).includes(id));
      if (prevAllCurrent) return otherPageSelected;
      return Array.from(new Set([...otherPageSelected, ...currentPageAccountIds]));
    });
  };

  const handleSave = async () => {
    try {
      const values = await detailForm.validateFields();

      const basePayload = {
        platform: normalizeOptionalString(values.platform),
        account_name: normalizeOptionalString(values.account_name),
        account_type: normalizeOptionalString(values.account_type),
        status: normalizeOptionalString(values.status),
        auth_config_id: normalizeOptionalString(values.auth_config_id),
        operator_user_ids: parseCommaList(values.operator_user_ids),
        operator_user_names: parseCommaList(values.operator_user_names),
      };

      const platformConfig = parseJsonObject(values.platform_config);
      if (platformConfig !== undefined) basePayload.platform_config = platformConfig;

      if (drawerMode === 'create') {
        const payload = {
          ...basePayload,
          account_id: normalizeOptionalString(values.account_id),
        };

        if (!payload.platform || !payload.account_name) {
          message.error('请填写平台与账号名称');
          return;
        }

        message.loading({ content: '正在保存...', key: 'saveAccount' });
        const response = await createPublishAccount(payload);
        if (response.status === 'success') {
          message.success({ content: `保存成功（${response.action || 'success'}）`, key: 'saveAccount' });
          const formValues = form.getFieldsValue();
          const searchParams = buildSearchParams(formValues, pagination.current, pagination.pageSize);
          await loadData(searchParams);

          // XHS: provision on create (ensure MCP instance exists, then open QR login flow).
          const platformCode = String(response?.account?.platform_code || '').trim();
          const platformDisplay = String(values?.platform || response?.account?.platform || '').trim();
          const accountId = response?.account_id;
          if ((platformCode === 'xhs' || platformDisplay === '小红书') && accountId) {
            try {
              setXhsProvisioning(true);
              message.loading({ content: '正在启动小红书 MCP...', key: 'xhsProvision' });
              const preferPortRaw = values?.xhs_prefer_port;
              const preferPort = preferPortRaw ? Number(preferPortRaw) : undefined;
              const ensureResp = await xhsMcpEnsure({
                account_id: accountId,
                ...(Number.isFinite(preferPort) ? { prefer_port: preferPort } : {}),
              });
              if (ensureResp?.status === 'success') {
                message.success({ content: '小红书 MCP 已启动，请扫码登录', key: 'xhsProvision' });
                setXhsProvisionAccount({
                  account_id: accountId,
                  account_name: response?.account?.account_name || values?.account_name || accountId,
                  platform: response?.account?.platform || platformDisplay || '小红书',
                  platform_code: 'xhs',
                });
              } else {
                const ensureMsg = resolveErrorMessage(ensureResp?.error_message || ensureResp?.reason, '启动 MCP 失败');
                message.error({ content: ensureMsg, key: 'xhsProvision' });
              }
            } catch (e) {
              message.error({ content: e?.message || String(e), key: 'xhsProvision' });
            } finally {
              setXhsProvisioning(false);
            }
            return;
          }

          setDrawerVisible(false);
          detailForm.resetFields();

          // Social platforms (douyin/kuaishou/weixin_channels): open QR login modal after create (macOS local).
          try {
            const platformCode = String(response?.account?.platform_code || '').trim();
            const accountId = response?.account_id;
            const loginAfterCreate = values?.login_after_create;
            if (accountId && loginAfterCreate !== false && ['douyin', 'kuaishou', 'weixin_channels'].includes(platformCode)) {
              setSocialProvisionAccount({
                account_id: accountId,
                account_name: response?.account?.account_name || accountId,
                platform: response?.account?.platform || platformCode,
                platform_code: platformCode,
                auto_start_token: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
              });
            }
          } catch (_) {
            // ignore
          }
        } else {
          message.error({ content: resolveErrorMessage(response.error_message, '保存失败'), key: 'saveAccount' });
        }
      } else {
        const accountId = normalizeOptionalString(values.account_id);
        if (!accountId) {
          message.error('缺少 account_id，无法更新');
          return;
        }

        const nextLocal = Object.fromEntries(
          Object.entries({
            account_id: accountId,
            ...basePayload,
            ...(platformConfig !== undefined ? { platform_config: platformConfig } : {}),
          }).filter(([, v]) => v !== undefined)
        );

        const payload = Object.fromEntries(
          Object.entries({
            account_id: accountId,
            ...basePayload,
          }).filter(([, v]) => v !== undefined)
        );

        message.loading({ content: '正在更新...', key: 'saveAccount' });
        const response = await updatePublishAccount(payload);
        if (response.status === 'success') {
          message.success({ content: '更新成功', key: 'saveAccount' });
          setDataSource((prev) => (prev || []).map((item) => (
            item?.account_id === accountId ? { ...item, ...nextLocal } : item
          )));
          recentlyUpdatedAccountsRef.current.set(accountId, { ts: Date.now(), patch: nextLocal });
          setDrawerVisible(false);
          detailForm.resetFields();
          const formValues = form.getFieldsValue();
          const searchParams = buildSearchParams(formValues, pagination.current, pagination.pageSize);
          // 等后端异步落库/缓存刷新，避免立刻刷新把本地更新覆盖成旧数据
          await new Promise((r) => setTimeout(r, 400));
          await loadData(searchParams, pagination.current, pagination.pageSize);
        } else {
          message.error({ content: resolveErrorMessage(response.error_message, '更新失败'), key: 'saveAccount' });
        }
      }
    } catch (error) {
      if (error?.message?.includes('platform_config JSON 解析失败')) {
        message.error(error.message);
        return;
      }
      if (error?.errorFields) return;
      console.error('保存失败:', error);
      message.error('保存失败，请稍后重试');
    }
  };

  const columns = [
    {
      title: '账号ID',
      dataIndex: 'account_id',
      key: 'account_id',
      width: 180,
      render: (value) => {
        if (!value) return '-';
        return (
          <span style={{ whiteSpace: 'normal', wordBreak: 'break-all', overflowWrap: 'anywhere' }}>
            {value}
          </span>
        );
      },
    },
    {
      title: '平台',
      dataIndex: 'platform',
      key: 'platform',
      width: 120,
      render: (value, record) => value || record.platform_code || '-',
    },
    {
      title: '平台编码',
      dataIndex: 'platform_code',
      key: 'platform_code',
      width: 140,
      ellipsis: true,
    },
    {
      title: '账号名称',
      dataIndex: 'account_name',
      key: 'account_name',
      width: 240,
      render: (value) => {
        if (!value) return '-';
        return (
          <span style={{ whiteSpace: 'normal', wordBreak: 'break-all', overflowWrap: 'anywhere' }}>
            {value}
          </span>
        );
      },
    },
    {
      title: '账号类型',
      dataIndex: 'account_type',
      key: 'account_type',
      width: 120,
      ellipsis: true,
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status) => {
        if (!status) return '-';
        const info = statusMap[status] || { text: status, color: 'default' };
        return <Tag color={info.color}>{info.text}</Tag>;
      },
    },
    {
      title: '运营人ID',
      dataIndex: 'operator_user_ids',
      key: 'operator_user_ids',
      width: 160,
      ellipsis: true,
      render: (value) => {
        const text = formatListField(value);
        if (!text) return '-';
        return <span title={text}>{text}</span>;
      },
    },
    {
      title: '运营人员名',
      dataIndex: 'operator_user_names',
      key: 'operator_user_names',
      width: 160,
      ellipsis: true,
      render: (value) => {
        const text = formatListField(value);
        if (!text) return '-';
        return <span title={text}>{text}</span>;
      },
    },
    {
      title: '上次发布',
      dataIndex: 'last_published_at',
      key: 'last_published_at',
      width: 180,
      render: (text) => formatDateTime(text),
    },
    {
      title: '累计发布数',
      dataIndex: 'total_published_count',
      key: 'total_published_count',
      width: 140,
      render: (value) => (value === null || value === undefined ? '-' : value),
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 180,
      render: (text) => formatDateTime(text),
    },
    {
      title: '更新时间',
      dataIndex: 'updated_at',
      key: 'updated_at',
      width: 180,
      render: (text) => formatDateTime(text),
    },
    {
      title: '操作',
      key: 'action',
      width: 200,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <DouyinAccountCheckButton
            account={record}
            label="检测"
            buttonProps={{ style: { padding: '0 4px' } }}
          />
          <KuaishouAccountCheckButton
            account={record}
            label="检测"
            buttonProps={{ style: { padding: '0 4px' } }}
          />
          <WeixinChannelsAccountCheckButton
            account={record}
            label="检测"
            buttonProps={{ style: { padding: '0 4px' } }}
          />
          <XhsAccountCheckButton
            account={record}
            label="检测"
            restartMcp
            buttonProps={{ style: { padding: '0 4px' } }}
            onSuccess={() => {
              const formValues = form.getFieldsValue();
              const searchParams = buildSearchParams(formValues, paginationRef.current.current, paginationRef.current.pageSize);
              loadData(searchParams, paginationRef.current.current, paginationRef.current.pageSize);
            }}
          />
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => openEditDrawer(record)}
            style={{ padding: '0 4px' }}
          >
            编辑
          </Button>
          <Button
            type="link"
            size="small"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record)}
            style={{ padding: '0 4px' }}
          >
            删除
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div className="page-shell">
      <Card
        className="page-card"
        bodyStyle={{ padding: 0 }}
      >
        <div className="page-panel">
          <div className="page-panel__top">
              <Form
                form={form}
                layout="horizontal"
                onFinish={handleFilterFinish}
                style={{ marginBottom: 0 }}
              >
            <Row gutter={[16, 16]} justify="space-between">
              <Col span={18}>
                <Row gutter={[16, 16]}>
                  <Col span={6}>
                    <Form.Item label="关键词" name="keyword" style={{ marginBottom: 0 }}>
                      <Input placeholder="搜索账号名称" style={{ width: '100%' }} />
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item label="平台" name="platforms" style={{ marginBottom: 0 }}>
                      <Select
                        mode="multiple"
                        allowClear
                        style={{ width: '100%' }}
                        placeholder="请选择平台"
                        options={platformOptions}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item label="状态" name="status" style={{ marginBottom: 0 }}>
                      <Select
                        mode="multiple"
                        allowClear
                        style={{ width: '100%' }}
                        placeholder="请选择状态"
                        options={[
                          { label: '活跃', value: 'active' },
                          { label: '已暂停', value: 'paused' },
                          { label: '未激活', value: 'inactive' },
                          { label: '已停用', value: 'suspended' },
                        ]}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item label="运营人ID" name="operator_user_id" style={{ marginBottom: 0 }}>
                      <Input placeholder="匹配 operator_user_ids" style={{ width: '100%' }} />
                    </Form.Item>
                  </Col>
                </Row>
              </Col>

              <Col span={6} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                <Space size="small" style={{ width: '100%', justifyContent: 'flex-end' }}>
                  <Button type="primary" style={{ width: 80 }} onClick={handleSearch}>
                    检索
                  </Button>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={openCreateDrawer}
                  >
                    新增账号
                  </Button>
                </Space>
              </Col>
            </Row>
              </Form>
            </div>

          <div className="page-panel__body">
              <Table
                columns={columns}
                dataSource={dataSource}
                rowKey="account_id"
                loading={loading}
                scroll={{ x: 2400 }}
                rowSelection={{
                  selectedRowKeys: selectedAccountKeys,
                  onChange: (keys) => {
                    setSelectedAccountKeys((prevKeys) => {
                      const currentPageIds = (dataSource || []).map((item) => item.account_id);
                      const otherPageSelected = (prevKeys || []).filter((key) => !currentPageIds.includes(key));
                      const allSelectedKeys = [...otherPageSelected, ...(keys || [])];
                      return Array.from(new Set(allSelectedKeys));
                    });
                  },
                }}
                pagination={false}
              />
            </div>

          <div className="page-panel__bottom" style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '12px 16px 0',
              background: '#fff',
            }}>
            <div>
              {selectedAccountKeys.length > 0 && (
                <Space>
                  <Button onClick={toggleSelectAllCurrentPage}>
                    {isAllCurrentPageSelected ? '取消全选(本页)' : '全选本页'}
                  </Button>
                  <span>已选择 {selectedAccountKeys.length} 项</span>
                  <Button danger icon={<DeleteOutlined />} onClick={handleBatchDelete}>
                    批量删除
                  </Button>
                  <Button onClick={() => setSelectedAccountKeys([])}>清空全部</Button>
                </Space>
              )}
            </div>
            <Pagination
              current={pagination.current}
              pageSize={pagination.pageSize}
              total={pagination.total}
              showTotal={(total) => `共 ${total} 条记录`}
              showSizeChanger
              showQuickJumper
              style={{ margin: 0 }}
              onChange={(page, pageSize) => {
                const formValues = form.getFieldsValue();
                const searchParams = buildSearchParams(formValues, page, pageSize);
                loadData(searchParams, page, pageSize);
              }}
              onShowSizeChange={(current, size) => {
                const formValues = form.getFieldsValue();
                const searchParams = buildSearchParams(formValues, 1, size);
                loadData(searchParams, 1, size);
              }}
            />
          </div>
        </div>
      </Card>

      <Drawer
        title={drawerMode === 'create' ? '新增账号' : '编辑账号'}
        placement="right"
        width={720}
        onClose={() => {
          setDrawerVisible(false);
          setXhsProvisioning(false);
          setXhsProvisionAccount(null);
          detailForm.resetFields();
        }}
        open={drawerVisible}
        loading={detailLoading}
        mask={true}
        maskStyle={{ backdropFilter: 'none', WebkitBackdropFilter: 'none' }}
        footer={(
          <div style={{ textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setDrawerVisible(false)}>取消</Button>
              <Button type="primary" onClick={handleSave} loading={detailLoading || xhsProvisioning} disabled={Boolean(xhsProvisionAccount)}>
                保存
              </Button>
            </Space>
          </div>
        )}
      >
        {drawerMode === 'create' && xhsProvisionAccount && (
          <div style={{
            padding: 12,
            marginBottom: 16,
            border: '1px solid #f0f0f0',
            borderRadius: 8,
            background: '#fafafa',
          }}
          >
            <div style={{ fontWeight: 600, marginBottom: 6 }}>小红书账号已创建</div>
            <div style={{ color: '#666', marginBottom: 8 }}>请在此抽屉内完成扫码登录，登录成功后将自动关闭。</div>
            <XhsAccountCheckButton
              account={xhsProvisionAccount}
              inline
              autoStart
              restartMcp
              onSuccess={() => {
                setXhsProvisionAccount(null);
                setDrawerVisible(false);
                detailForm.resetFields();
                const formValues = form.getFieldsValue();
                const searchParams = buildSearchParams(formValues, pagination.current, pagination.pageSize);
                loadData(searchParams, pagination.current, pagination.pageSize);
              }}
            />
          </div>
        )}
        <Form
          form={detailForm}
          layout="vertical"
          style={{ padding: '8px 0' }}
        >
          <Row gutter={[16, 0]}>
            <Col span={12}>
              <Form.Item
                label="账号ID"
                name="account_id"
                rules={drawerMode === 'edit' ? [{ required: true, message: '缺少账号ID' }] : []}
              >
                <Input placeholder={drawerMode === 'create' ? '可选：不填则自动生成' : ''} disabled={drawerMode === 'edit'} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="平台"
                name="platform"
                rules={[{ required: true, message: '请选择平台' }]}
              >
                <Select
                  allowClear
                  placeholder="请选择平台"
                  options={platformOptions}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={[16, 0]}>
            <Col span={12}>
              <Form.Item
                label="账号名称"
                name="account_name"
                rules={[{ required: true, message: '请输入账号名称' }]}
              >
                <Input placeholder="用于展示与检索" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="账号类型" name="account_type">
                <Select
                  allowClear
                  placeholder="请选择账号类型"
                  options={[
                    { label: '个人', value: '个人' },
                    { label: '机构', value: '机构' },
                  ]}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={[16, 0]}>
            <Col span={12}>
              <Form.Item label="状态" name="status">
                <Select
                  allowClear
                  placeholder="请选择状态（默认 active）"
                  options={[
                    { label: '活跃', value: 'active' },
                    { label: '已暂停', value: 'paused' },
                    { label: '未激活', value: 'inactive' },
                    { label: '已停用', value: 'suspended' },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Auth配置ID" name="auth_config_id">
                <Input placeholder="可选" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={[16, 0]}>
            <Col span={12}>
              <Form.Item label="运营人ID（逗号分隔）" name="operator_user_ids">
                <Input placeholder="例如：aispire,alice" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="运营人昵称（逗号分隔）" name="operator_user_names">
                <Input placeholder="例如：张三,李四" />
              </Form.Item>
            </Col>
          </Row>

          {drawerMode === 'create' && (
            <Form.Item
              noStyle
              shouldUpdate={(prev, cur) => prev.platform !== cur.platform}
            >
              {({ getFieldValue }) => {
                const p = String(getFieldValue('platform') || '').trim();
                if (!['抖音', '快手', '微信视频号'].includes(p)) return null;
                return (
                  <Form.Item
                    label="创建后扫码登录"
                    name="login_after_create"
                    valuePropName="checked"
                    tooltip="在本机 Mac 上弹出二维码扫码登录，成功后自动上传 OSS 并回写账号配置"
                  >
                    <Switch defaultChecked />
                  </Form.Item>
                );
              }}
            </Form.Item>
          )}

          {drawerMode === 'create' && (
            <Form.Item
              noStyle
              shouldUpdate={(prev, cur) => prev.platform !== cur.platform}
            >
              {({ getFieldValue }) => {
                const p = String(getFieldValue('platform') || '').trim();
                if (p !== '小红书') return null;
                return (
                  <Form.Item
                    label="小红书端口（可选，18060-18160）"
                    name="xhs_prefer_port"
                    tooltip="不填则自动分配端口；填了则尽量使用该端口（若被占用会失败）"
                  >
                    <InputNumber min={18060} max={18160} style={{ width: '100%' }} placeholder="例如：18063" />
                  </Form.Item>
                );
              }}
            </Form.Item>
          )}

          <Form.Item label="平台配置（platform_config，JSON object）" name="platform_config">
            <Input.TextArea rows={10} placeholder='例如：{"douyin_cookie_file":"https://.../dy.json"}' />
          </Form.Item>
        </Form>
      </Drawer>

      {socialProvisionAccount && (
        <SocialAccountLoginButton
          key={`social-provision-${socialProvisionAccount.account_id}-${socialProvisionAccount.auto_start_token || 'default'}`}
          account={socialProvisionAccount}
          autoStart
          autoStartToken={socialProvisionAccount.auto_start_token || 'create-social-account'}
          buttonProps={{ style: { display: 'none' } }}
          onSuccess={() => {
            setSocialProvisionAccount(null);
            const formValues = form.getFieldsValue();
            const searchParams = buildSearchParams(formValues, pagination.current, pagination.pageSize);
            loadData(searchParams, pagination.current, pagination.pageSize);
          }}
        />
      )}
    </div>
  );
};

export default AccountManagement;
